import { useQuery } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
} from 'react';
import {
  AngleLeftIcon,
  AngleRightIcon,
  AscendingIcon,
  DecendingIcon,
  SortingIcon,
  RefreshIcon,
  FilterIcon,
} from '../../helpers/icons';
import { Button } from '../Button';
import { ColumnFilter, isMeaningful } from './ColumnFilter';
import { makeColumnFilterValuesFn } from './columnFilterValues';
import { TableActions } from './TableActions';
import { NoRecordFound } from '../NoRecordFound';
import { Spinner } from '../Loaders';
import { getObjectLength } from '../../helpers/functions';
import { formatDate } from '../../helpers/formatters';

const PAGE_NUMBER_LIST_LIMIT = 5;

export const TableManager = React.forwardRef(
  (
    {
      queryKey,
      queryFn,
      payloadForQuery = {},
      isQueryEnabled = true,
      columns = [],
      setSelectedRows = () => {},
      selectRowWith = 'id',
      tableContainerClass = '',
      // Size the table to its rows instead of to a slice of the viewport, so
      // the pagination sits directly under the last row. Off by default: every
      // existing table keeps the height it has today.
      fitToContent = false,
      showSorting = true,
      showPagination = true,
      showRefetchButton = true,
      showFetchingLoader = true,
      actionsArray = [],
      jsonDataForTable = [],
      globalSearch = '',
      selectCBForTableData = (tblData) => tblData?.data?.data,
      search_filters = [],
      setTableData = () => {},
      onFetchComplete = () => {},
      scrollClass = 'table--responsive--full-scroll',
      showFooter = false,
      stickPaginationBottom = false,
      refetchInterval = false,
      // Excel-style per-header filters. A column opts in with
      // `meta.filter = { type: 'values' | 'text' | 'number' | 'date' }`; the
      // conditions go to the server with the listing, so they narrow the whole
      // report rather than the rows currently on screen. Any listing API
      // supports them already -- queryOption folds `column_filters` into its
      // where clause -- so a table only has to say where a 'values' column's
      // checkbox list comes from: `columnFilterSource` is the model or table
      // behind this listing ('Message', 'user_invoices'), which the shared
      // endpoint scopes the way that table's listing is scoped, or the name of
      // one of its presets where the listing is narrowed by something no column
      // can express. `columnFilterValuesFn` stays for a listing neither covers.
      columnFilterSource = '',
      columnFilterValuesFn = null,
      columnFilterValuesQueryKey = '',
    },
    ref,
  ) => {
    const [rowSelection, setRowSelection] = useState({});
    const [maxPageNumberListLimit, setMaxPageNumberListLimit] = useState(5);
    const [minPageNumberListLimit, setMinPageNumberListLimit] = useState(0);
    const [sorting, setSorting] = useState([]);
    // Keyed by column id, so re-filtering a column replaces its condition
    // instead of stacking a second one.
    const [columnFilters, setColumnFilters] = useState({});
    const [{ pageIndex, pageSize }, setPagination] = useState({
      pageIndex: 0,
      pageSize: 50,
    });

    // A source key is bound once, so the query function is stable and the
    // panel's value list is not re-fetched on every render.
    const resolvedColumnFilterValuesFn = useMemo(
      () =>
        columnFilterValuesFn ||
        (columnFilterSource
          ? makeColumnFilterValuesFn(columnFilterSource)
          : null),
      [columnFilterValuesFn, columnFilterSource],
    );

    const memoizedColumns = useMemo(() => columns, [columns]);
    const pagination = useMemo(
      () => ({ pageIndex, pageSize }),
      [pageIndex, pageSize],
    );

    const columnFilterList = useMemo(
      () => Object.values(columnFilters).filter(Boolean),
      [columnFilters],
    );
    const stringColumnFilters = JSON.stringify(columnFilterList);
    const activeColumnFilterCount = columnFilterList.length;

    const stringPayloadForQuery = JSON.stringify(payloadForQuery);

    let payload = useMemo(
      () => ({
        page:
          globalSearch?.length > 2 && stringPayloadForQuery?.length > 21
            ? 1
            : pageIndex + 1,
        length: pageSize,
        search_filters,
        column_filters: columnFilterList,
        global_search: globalSearch?.length > 2 ? globalSearch : '',
        sort: sorting[0]
          ? { [sorting[0]['id']]: sorting[0].desc ? 'desc' : 'asc' }
          : null,
        ...payloadForQuery,
      }),
      [
        globalSearch,
        pageIndex,
        pageSize,
        payloadForQuery,
        sorting,
        search_filters,
        stringPayloadForQuery,
        columnFilterList,
      ],
    );

    // A value list has to be built against the same rows the listing shows,
    // minus the filter on its own column -- that exclusion happens server-side,
    // so the whole current filter set is sent as-is.
    const columnFilterContext = useMemo(
      () => ({
        search_filters,
        column_filters: columnFilterList,
        // Spread last, exactly as the listing payload does: a page that scopes
        // the report through payloadForQuery (the rated/unrated switch here)
        // has to scope the value lists the same way, or a dropdown would offer
        // values from rows the table is not showing.
        ...payloadForQuery,
      }),
      [payloadForQuery, search_filters, columnFilterList],
    );

    const handleColumnFilterChange = useCallback((columnId, filter) => {
      setColumnFilters((previous) => {
        const next = { ...previous };
        if (filter && isMeaningful(filter)) {
          next[columnId] = filter;
        } else {
          delete next[columnId];
        }
        return next;
      });
    }, []);

    const {
      isLoading,
      isFetching,
      data: apiResponse,
      refetch,
    } = useQuery({
      queryKey: [queryKey, payload],
      queryFn: () => queryFn(payload),
      enabled: isQueryEnabled,
      refetchInterval,
    });

    useEffect(() => {
      if (apiResponse && getObjectLength(apiResponse?.data) > 0) {
        setTableData(apiResponse?.data);
      }
      if (apiResponse && !isFetching) onFetchComplete();
      // setSelectedRows([]);
      // setRowSelection({});
    }, [isLoading, isFetching, apiResponse, onFetchComplete]);

    const tableInstance = useReactTable({
      debugAll: false,
      data: selectCBForTableData(apiResponse) || jsonDataForTable,
      columns: memoizedColumns || [],
      state: {
        pagination,
        rowSelection,
        sorting,
      },
      manualPagination: true,
      pageCount: apiResponse?.data.lastPage ?? 0,
      getCoreRowModel: getCoreRowModel(),
      onPaginationChange: setPagination,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
    });

    useEffect(() => {
      if (globalSearch?.length > 2 || stringPayloadForQuery?.length > 21) {
        tableInstance.setPageIndex(0);
      }
    }, [stringPayloadForQuery, globalSearch, tableInstance]);

    // Narrowing a column usually leaves fewer pages than before, so page 7 of
    // the old result set would come back empty.
    useEffect(() => {
      tableInstance.setPageIndex(0);
    }, [stringColumnFilters, tableInstance]);

    useImperativeHandle(ref, () => {
      return {
        clearRowSelection() {
          tableInstance.resetRowSelection(true);
        },
      };
    }, [tableInstance]);

    useEffect(() => {
      if (rowSelection && Object.keys(rowSelection).length > 0) {
        const rowData = tableInstance.getSelectedRowModel()?.flatRows;
        const arr = [];
        for (const element of rowData) {
          if (element?.original?.[selectRowWith])
            arr.push(element.original[selectRowWith].toString());
        }
        setSelectedRows(arr);
      }
      return () => {
        setSelectedRows([]);
      };
    }, [rowSelection, selectRowWith, setSelectedRows, tableInstance]);

    const handleNextPage = useCallback(() => {
      tableInstance?.nextPage();
      if (pageIndex === tableInstance?.getPageCount() - 1) {
        return false;
      } else {
        if (pageIndex + 2 > maxPageNumberListLimit) {
          setMaxPageNumberListLimit(
            maxPageNumberListLimit + PAGE_NUMBER_LIST_LIMIT,
          );
          setMinPageNumberListLimit(
            minPageNumberListLimit + PAGE_NUMBER_LIST_LIMIT,
          );
        }
      }
    }, [
      maxPageNumberListLimit,
      minPageNumberListLimit,
      pageIndex,
      tableInstance,
    ]);

    const handlePreviousPage = useCallback(() => {
      tableInstance?.previousPage();
      if (pageIndex === 0) {
        return;
      } else {
        if (pageIndex % PAGE_NUMBER_LIST_LIMIT === 0) {
          setMaxPageNumberListLimit(
            maxPageNumberListLimit - PAGE_NUMBER_LIST_LIMIT,
          );
          setMinPageNumberListLimit(
            minPageNumberListLimit - PAGE_NUMBER_LIST_LIMIT,
          );
        }
      }
    }, [
      maxPageNumberListLimit,
      minPageNumberListLimit,
      pageIndex,
      tableInstance,
    ]);

    const handleLastPage = useCallback(() => {
      tableInstance?.setPageIndex(tableInstance?.getPageCount() - 1);
      const min = tableInstance.getPageCount() - 5;
      const max = tableInstance.getPageCount();
      setMaxPageNumberListLimit(max);
      setMinPageNumberListLimit(min);
    }, [tableInstance]);

    const handleFirstPage = useCallback(() => {
      tableInstance?.setPageIndex(0);
      setMinPageNumberListLimit(0);
      setMaxPageNumberListLimit(5);
    }, [tableInstance]);

    const isQueryLoading = isLoading || (showFetchingLoader && isFetching);
    // const isQueryLoading = [isLoading, isFetching, status === 'pending'].some((val) => val);
    const rowsLength = tableInstance.getRowModel().rows
      ? tableInstance.getRowModel().rows.length
      : 0;
    const isContentHeight =
      fitToContent ||
      String(tableContainerClass || '').includes('table--responsive--content');
    return (
      <div
        className={`d--flex flex--column ${
          stickPaginationBottom ? 'h--full' : ''
        } ${isContentHeight || stickPaginationBottom ? 'p-t--xs p-b--0' : 'p--sm'}`}
        style={
          stickPaginationBottom
            ? { height: '100%', minHeight: '100%' }
            : undefined
        }
      >
        <div
          className={`table--responsive ${
            stickPaginationBottom || isContentHeight ? '' : scrollClass
          } ${stickPaginationBottom ? '' : tableContainerClass}`}
          style={
            stickPaginationBottom
              ? { flex: '1 1 auto', minHeight: 0, maxHeight: 'none', overflow: 'auto' }
              : undefined
          }
        >
          <table className="table border-bottom--black-100">
            <thead>
              {tableInstance?.getHeaderGroups()?.map((headerGroup) => (
                <tr key={headerGroup?.id}>
                  {headerGroup?.headers?.map((header) => {
                    const { meta } = header.column.columnDef;
                    const {
                      isSortable = true,
                      width = '',
                      textAlign = 'left',
                      headerStyle = {},
                      filter: filterConfig = null,
                    } = meta || {};
                    if (header.id === 'select' || header.id === 'sr') {
                      return (
                        <th
                          key={header?.id}
                          colSpan={header.colSpan}
                          style={{
                            width: width || '4%',
                            textAlign: 'center',
                            ...headerStyle,
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </th>
                      );
                    }
                    if (header.column.id === 'action') {
                      return (
                        <th
                          className="text--c"
                          key={header?.id}
                          colSpan={header.colSpan}
                          style={{
                            textAlign: 'right',
                            width: width || '2%',
                            ...headerStyle,
                          }}
                        >
                          <div className="d--flex justify-content--end">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        </th>
                      );
                    }
                    return (
                      <th
                        key={header?.id}
                        colSpan={header.colSpan}
                        style={{
                          textAlign,
                          width,
                          ...headerStyle,
                        }}
                        onClick={
                          showSorting && isSortable
                            ? header.column.getToggleSortingHandler()
                            : () => {}
                        }
                      >
                        <div className="d--flex gap--xs align-items--center">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {showSorting && isSortable && (
                            // Sorting lives on the header's own arrow, next
                            // to the filter control: it shows the current
                            // direction at a glance, and clicking anywhere in
                            // the cell cycles it.
                            <span
                              className={`c--pointer d--flex ${
                                header.column.getIsSorted()
                                  ? 'text--primary'
                                  : ''
                              }`}
                            >
                              {{
                                asc: <AscendingIcon width={12} />,
                                desc: <DecendingIcon width={12} />,
                              }[header.column.getIsSorted()] ?? (
                                <SortingIcon width={12} />
                              )}
                            </span>
                          )}
                          {filterConfig && (
                            <ColumnFilter
                              {...{
                                column: header.column.id,
                                label: filterConfig.label || header.column.id,
                                config: filterConfig,
                                value: columnFilters[header.column.id] || null,
                                onChange: (filter) =>
                                  handleColumnFilterChange(
                                    header.column.id,
                                    filter,
                                  ),
                                valuesFn: resolvedColumnFilterValuesFn,
                                valuesQueryKey:
                                  columnFilterValuesQueryKey ||
                                  `${queryKey}ColumnValues`,
                                context: columnFilterContext,
                              }}
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {isQueryLoading ? (
                <tr>
                  <td colSpan={columns?.length}>
                    <div className="d--flex align-items--center justify-content--center w--full h-min--200">
                      <Spinner size="md" />
                    </div>
                  </td>
                </tr>
              ) : tableInstance?.getRowModel()?.rows?.length > 0 ? (
                tableInstance?.getRowModel()?.rows?.map((row, index) => {
                  return (
                    <tr key={row?.id}>
                      {row?.getVisibleCells()?.map((cell) => {
                        const { meta } = cell.column.columnDef;
                        const {
                          width = '',
                          headerStyle = {},
                          isCustom = false,
                          cellStyle = {},
                          textAlign = 'left',
                        } = meta || {};
                        if (cell.column.id === 'sr') {
                          return (
                            <td
                              key={cell?.id}
                              style={{
                                textAlign,
                                ...cellStyle,
                              }}
                            >
                              {index + 1}
                            </td>
                          );
                        }
                        if (
                          [
                            'created_at',
                            "last_sent_at",
                            'updated_at',
                            'purchase_date',
                            'date',
                            'start_date',
                            'allocation_date',
                          ].includes(cell.column.id)
                        ) {
                          return (
                            <td
                              key={cell?.id}
                              style={{
                                textAlign,
                                ...cellStyle,
                              }}
                            >
                              {formatDate(cell.getValue())}
                            </td>
                          );
                        }
                        if (cell.column.id === 'select') {
                          return (
                            <td
                              key={cell?.id}
                              style={{
                                width: width || '4%',
                                textAlign: 'center',
                                ...headerStyle,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          );
                        }
                        if (cell.column.id === 'action') {
                          if (isCustom) {
                            return (
                              <td
                                key={cell?.id}
                                style={{
                                  textAlign,
                                  ...cellStyle,
                                }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </td>
                            );
                          }
                          return (
                            <td
                              key={cell?.id}
                              style={{
                                textAlign,
                                ...cellStyle,
                              }}
                            >
                              {actionsArray.length > 0 ? (
                                <TableActions
                                  rowData={row.original}
                                  actions={actionsArray}
                                />
                              ) : (
                                '---'
                              )}
                            </td>
                          );
                        }
                        return (
                          <td
                            key={cell?.id}
                            style={{
                              textAlign,
                              ...cellStyle,
                            }}
                          >
                            {cell.getValue() === null ||
                            cell.getValue() === undefined ||
                            cell.getValue() === ''
                              ? '---'
                              : flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={tableInstance?.getAllFlatColumns()?.length}
                    align="center"
                  >
                    <NoRecordFound />
                  </td>
                </tr>
              )}
            </tbody>
            {showFooter && !isQueryLoading && (
              <tfoot>
                {tableInstance.getFooterGroups().map((footerGroup) => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          width: '3%',
                          textAlign: 'left',
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.footer,
                          header.getContext(),
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
        <div
          className={`w--full d--flex align-items--center justify-content--between paginationTable ${
            stickPaginationBottom
              ? 'm-t--auto p-t--sm p-b--sm border-top--black-100'
              : isContentHeight
                ? // A table sized to its rows has no page padding under it, so
                  // the pagination would otherwise sit flush against the last
                  // row. Keyed on fitToContent, so tables using
                  // table--responsive--content keep the spacing they have.
                  fitToContent
                  ? 'm-t--md p-t--sm p-b--xs'
                  : 'm-t--xs p-t--xs p-b--xs'
                : 'm-t--md p--sm'
          }`}
        >
          {showPagination && (
            <>
              <div className="d--flex gap--sm align-items--center w--full">
                {/* <span>
                  <span className="font--500">Total</span>
                  &nbsp;
                  <span className="font--600 text--primary">
                    {apiResponse?.data?.totalRecords || 0}
                  </span>
                  {' '}
                  <span className="font--500">Records</span>
                </span> */}

                <span>
                  <span className="font--600">Showing</span>{' '}
                  {pageIndex * pageSize + 1}-{pageIndex * pageSize + rowsLength}{' '}
                  of {apiResponse?.data?.totalRecords}
                </span>

                <span>|</span>
                <span>
                  <span className="font--600">Page</span>
                  &nbsp;
                  <span>
                    {tableInstance?.getState()?.pagination?.pageIndex + 1} of{' '}
                    {tableInstance?.getPageCount()}
                  </span>
                </span>
                <span>|</span>
                <span className="d--flex gap--sm align-items--center w-max--300 w--full">
                  <span className="font--600">Show</span>
                  <select
                    className="form--control w--full h-min--28 h-max--28 w-max--60 radius--sm p-l--xs p-r--xs"
                    value={tableInstance?.getState()?.pagination?.pageSize}
                    onChange={(e) => tableInstance?.setPageSize(e.target.value)}
                  >
                    {[50, 100, 200, 500].map((page, index) => (
                      <option value={page} key={index}>
                        {page}
                      </option>
                    ))}
                  </select>
                  <span className="w--full d--flex">records per page</span>
                </span>
              </div>
              {!isQueryLoading &&
                tableInstance?.getRowModel()?.rows?.length > 0 && (
                  <div className="d--flex align-items--center justify-content--end">
                    <div className="d--flex gap--xs align-items--center">
                      <Button
                        btnClasses="bg--black-100 c--pointer h-min--28 h-max--28 r-t-left--sm w-max--28 w-min--28 d--flex align-items--center justify-content--center"
                        type="button"
                        variant="primary"
                        color="black-600"
                        disabled={!tableInstance?.getCanPreviousPage()}
                        onClick={() => handleFirstPage()}
                      >
                        <AngleLeftIcon width={18} height={18} />
                      </Button>
                      <Button
                        btnClasses="bg--black-100 c--pointer h-min--28 h-max--32 radius--sm w-max--28 w-min--28 d--flex align-items--center justify-content--center"
                        type="button"
                        variant="primary"
                        color="black-600"
                        disabled={!tableInstance?.getCanPreviousPage()}
                        onClick={() => handlePreviousPage()}
                      >
                        <AngleLeftIcon width={18} height={18} />
                      </Button>
                      <div className="d--flex gap--xs align-item--center">
                        {tableInstance?.getPageOptions()?.map((page, index) => {
                          const number = page + 1;
                          if (
                            number < maxPageNumberListLimit + 1 &&
                            number > minPageNumberListLimit
                          ) {
                            return (
                              <div
                                className={`c--pointer h-min--28 h-max--32 radius--sm w-max--28 w-min--28 d--flex align-items--center justify-content--center border-full--black-50 ${
                                  tableInstance?.getState()?.pagination
                                    ?.pageIndex === index
                                    ? 'bg--primary text--white'
                                    : ''
                                }`}
                                key={index}
                                onClick={() =>
                                  tableInstance?.setPageIndex(index)
                                }
                              >
                                {page + 1}
                              </div>
                            );
                          } else {
                            return null;
                          }
                        })}
                      </div>
                      <Button
                        btnClasses="bg--black-100 c--pointer h-min--28 h-max--32 radius--sm w-max--28 w-min--28 d--flex align-items--center justify-content--center"
                        type="button"
                        variant="primary"
                        color="black-600"
                        disabled={!tableInstance?.getCanNextPage()}
                        onClick={() => handleNextPage()}
                      >
                        <AngleRightIcon width={18} height={18} />
                      </Button>
                      <Button
                        btnClasses="bg--black-100 c--pointer h-min--28 h-max--32 radius--sm w-max--28 w-min--28 d--flex align-items--center justify-content--center"
                        type="button"
                        variant="primary"
                        color="black-600"
                        disabled={!tableInstance?.getCanNextPage()}
                        onClick={() => handleLastPage()}
                      >
                        <AngleRightIcon width={18} height={18} />
                      </Button>
                    </div>
                  </div>
                )}
            </>
          )}
          {activeColumnFilterCount > 0 && (
            <button
              type="button"
              className="column-filter__btn column-filter__btn--ghost d--flex align-items--center gap--xs white-space--nowrap"
              onClick={() => setColumnFilters({})}
            >
              <FilterIcon width={11} height={11} isActive />
              Clear {activeColumnFilterCount}{' '}
              {activeColumnFilterCount === 1 ? 'filter' : 'filters'}
            </button>
          )}
          {isQueryEnabled && showRefetchButton && (
            <Button
              btnClasses="c--pointer h-min--28 h-max--28 r-t-left--sm w-max--28 w-min--28 d--flex align-items--center justify-content--center"
              type="button"
              variant="transparent"
              color="primary"
              data-tooltip="Refresh"
              tooltip-position="left"
              onClick={() => isQueryEnabled && !isQueryLoading && refetch()}
            >
              {isQueryLoading ? (
                <Spinner />
              ) : (
                <RefreshIcon width={18} height={18} />
              )}
            </Button>
          )}
        </div>
      </div>
    );
  },
);
